/* eslint-disable arrow-body-style */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Segment } from 'tea-component';

import type { INormalTabConfig } from './_type';
import { GAME_TYPE_ICON_MAP } from '../../../config/constant';
import GameIconRenderer from './GameIconRenderer';
import RenderImage from './RenderImage';
import { extractMetricDataKey, generateOptions } from '../AIContent/_utils';
import SvgIcon from '@home/components/SvgIcon';
import { getName } from '@home/utils/common';
import { getDataSourceActiveIcon, getAgentSourceIcon } from '@/pages/aigc/utils/getIcon';
import { channelInfo } from '@op/components/ChannelLabel';
interface Props {
  config: INormalTabConfig;
  value: string;
  onChange: (value: { [key: string]: string; }) => void;
  dataKey: string;
  name: string;
  valueList: { [key: string]: string; }[];
}
const SegmentCom: React.FC<Props> = (props) => {
  const { dataKey, name, valueList, value: propsValue, onChange } = props || {};
  const segmentContentRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);

  useEffect(() => {
    const container = segmentContentRef.current;
    if (!container) return;

    const measure = () => {
      requestAnimationFrame(() => {
        const realScrollWidth = container.scrollWidth;
        const realClientWidth = container.clientWidth;
        if (realScrollWidth > realClientWidth) {
          setCanScroll(true);
        } else {
          setCanScroll(false);
        }
        checkScrollPosition();
      });
    };

    // 新增：滚动事件处理
    const onScroll = () => {
      checkScrollPosition();
    };

    // 使用 ResizeObserver 替代 resize 事件
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);

    // 添加滚动事件监听
    container.addEventListener('scroll', onScroll);

    // 初始测量延迟 100ms 确保渲染完成
    const timer = setTimeout(measure, 100);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', onScroll); // 清理时移除监听
      clearTimeout(timer);
    };
  }, []);

  const options = useMemo(() => {
    const renderOptions =
      dataKey !== 'metrics'
        ? generateOptions(valueList, dataKey)
        : valueList?.map((items: any) => {
          const key =
            extractMetricDataKey(items?.data_key) || items?.metric_key || '';
          return {
            ...items,
            value: key,
            label: items?.name || key,
          };
        });

    return renderOptions?.map(item => {
      const { value, label, icon, icon_active } = item || {};
      const isActive = value === propsValue;
      let imgSrc = null;
      if (dataKey === 'game_type') {
        imgSrc = GAME_TYPE_ICON_MAP?.[value];
      } else if (dataKey === 'source') {
        imgSrc = getName(isActive, getDataSourceActiveIcon(value), getAgentSourceIcon(value));
      } else if (dataKey === 'channel_name' || dataKey === 'channel') {
        imgSrc = channelInfo(value)?.img;
      }
      return {
        text: (
          <div className="dba-may-ask-chart-segment-cascade-item">
            {(icon || imgSrc) && (
              dataKey === 'game_type' ? (
                <GameIconRenderer icon={getName(isActive, icon_active, icon)} defaultIcon={imgSrc} />
              ) : (
                <RenderImage src={getName(isActive, icon_active || imgSrc, icon || imgSrc)} className="dba-may-ask-chart-segment-cascade-item-img" />
              )
            )}
            <div>{label}</div>
          </div>
        ),
        value,
      };
    });
  }, [dataKey, valueList]);

  const checkScrollPosition = () => {
    const container = segmentContentRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    // 判断是否在最左或最右
    setIsAtStart(scrollLeft === 0);
    setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1); // 容错 1px
  };

  const scrollLeftHandler = () => {
    const container = segmentContentRef.current;
    if (!container) return;

    // 判断是否可滚动
    if (container.scrollWidth > container.clientWidth) {
      container.scrollTo({
        left: container.scrollLeft - 100, // 向左滚动 200px
        behavior: 'smooth', // 平滑滚动
      });
    }
  };

  const scrollRightHandler = () => {
    const container = segmentContentRef.current;
    if (!container) return;

    // 判断是否可滚动
    if (container.scrollWidth > container.clientWidth) {
      container.scrollTo({
        left: container.scrollLeft + 100, // 向右滚动 200px
        behavior: 'smooth', // 平滑滚动
      });
    }
  };

  if (options?.length <= 1) {
    return null;
  }

  return (
    <div className='dba-normal-segement dba-ask-chart-segement'>
      <button className="tea-btn tea-btn--weak dba-segment-title" type="button" role="radio" aria-checked="true">
        <div className='dba-may-ask-chart-segment-cascade-item'>{name || ''}</div>
      </button>

      {/* 左侧按钮 */}
      {canScroll && (
        <div
          onClick={!isAtStart ? scrollLeftHandler : undefined}
          className={isAtStart ? 'dba-scroll-icon dba-scroll-icon-disabled' : 'dba-scroll-icon'}
        >
          {/* <img src={iconLeft} alt="" /> */}
          <SvgIcon className='dba-scroll-icon' type={'agent-left-arrow'} />
        </div>
      )}

      {/* 滚动容器 */}
      <div className='dba-segement-content' ref={segmentContentRef}>
        <Segment
          options={options}
          value={propsValue}
          onChange={(val) => onChange({ [dataKey]: val })}
        />
      </div>

      {/* 右侧按钮 */}
      {canScroll && (
        <div
          onClick={!isAtEnd ? scrollRightHandler : undefined}
          className={isAtEnd ? 'dba-scroll-icon dba-scroll-icon-disabled' : 'dba-scroll-icon'}
        >
          {/* <img src={iconRight} alt="" /> */}
          <SvgIcon className='dba-scroll-icon' type={'agent-right-arrow'} />
        </div>
      )}
    </div>
  );
};

export default SegmentCom;